import { AppSelect } from '../ui/AppSelect';
import React, { useMemo, useRef, useState } from "react";
import { Check, Download, Loader2, RefreshCw, Sparkles, User, UserPlus, Wand2 } from "lucide-react";
import { downloadBlob } from "../../utils/downloadUtils";
import { downloadFile } from "../../utils/fileUtils";

const DEFAULT_LAYOUT_MODE = "face_turnaround_sheet";
const LAYOUT_OPTIONS = [
  { value: "face_turnaround_sheet", zh: "大脸三视图", en: "Face + turnaround" },
  { value: "four_grid_sheet", zh: "四宫格定妆表", en: "Four-panel sheet" },
  { value: "standard_reference", zh: "标准定妆照", en: "Standard portrait" },
  { value: "turnaround_sheet", zh: "多视角转面图", en: "Turnaround sheet" },
  { value: "raw_prompt", zh: "无版式", en: "No layout" },
  { value: "advanced_design_board", zh: "高级角色设计板", en: "Advanced design board" },
];

const ASPECT_OPTIONS = ["9:16", "3:4", "1:1", "4:3", "16:9"];
const RATIO_LAYOUTS = new Set(["standard_reference", "raw_prompt"]);

const copyPlan = (plan = {}) => ({
  ...plan,
  name: String(plan.name || ""),
  aliases: Array.isArray(plan.aliases) ? plan.aliases : [],
  concept: String(plan.concept || ""),
  imagePrompt: String(plan.imagePrompt || plan.image_prompt || ""),
  identity: plan.identity && typeof plan.identity === "object" ? plan.identity : {},
  visualTraits: Array.isArray(plan.visualTraits)
    ? plan.visualTraits
    : Array.isArray(plan.visual_traits) ? plan.visual_traits : [],
});

const newSeed = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const RandomCastingWorkspace = ({
  theme,
  overseasProject = false,
  imageQualityTier,
  onCreatePlan,
  onGeneratePreview,
  onAddToWorkshop,
  onViewCharacter,
}) => {
  const text = useMemo(() => overseasProject ? {
    title: "Random casting",
    blind: "Blind box",
    constrained: "Constrained",
    gender: "Gender",
    age: "Age range",
    region: "Ethnicity / region",
    role: "Occupation / role",
    direction: "Additional direction",
    directionPlaceholder: "e.g. energetic but not an influencer face; looks reliable under pressure",
    regionPlaceholder: "Optional, e.g. Southeast Asian, Northern European",
    rolePlaceholder: "Optional, e.g. forensic accountant, night-shift nurse",
    random: "Random",
    female: "Female",
    male: "Male",
    open: "Open",
    teen: "Teen",
    young: "Young adult",
    middle: "Middle-aged",
    senior: "Senior",
    layout: "Character sheet",
    ratio: "Aspect ratio",
    quality: "Image quality",
    planOnly: "Create casting only",
    planAndImage: "Generate candidate",
    planning: "Casting...",
    generating: "Generating candidate...",
    empty: "No casting result yet",
    emptyHint: "Leave every field empty for a full blind-box casting.",
    concept: "Casting rationale",
    identity: "Identity",
    traits: "Stable visual anchors",
    name: "Character name",
    aliases: "Aliases",
    prompt: "Image prompt",
    reroll: "Reroll casting",
    generate: "Generate candidate",
    candidateReady: "Candidate ready · not added to this project",
    add: "Add to character workshop",
    adding: "Adding...",
    added: "Added to character workshop",
    download: "Download",
    downloading: "Downloading...",
    view: "Open character asset",
    failed: "Generation failed",
  } : {
    title: "随机选角",
    blind: "完全盲盒",
    constrained: "约束随机",
    gender: "性别",
    age: "年龄段",
    region: "人种 / 地域",
    role: "职业 / 身份",
    direction: "补充方向",
    directionPlaceholder: "例如：青春活力但不要网红脸；遇事可靠，带一点不服输",
    regionPlaceholder: "选填，例如：川渝本地人、东南亚华裔",
    rolePlaceholder: "选填，例如：法医、夜班护士、县城汽修工",
    random: "随机",
    female: "女性",
    male: "男性",
    open: "不限定",
    teen: "少年",
    young: "青年",
    middle: "中年",
    senior: "老年",
    layout: "生成版式",
    ratio: "图片比例",
    quality: "图片清晰度",
    planOnly: "只生成人设",
    planAndImage: "随机候选图",
    planning: "正在选角...",
    generating: "正在生成候选角色...",
    empty: "还没有选角结果",
    emptyHint: "所有约束留空，就是完全随机的角色盲盒。",
    concept: "构思解析",
    identity: "人物身份",
    traits: "稳定辨识点",
    name: "角色名称",
    aliases: "别名",
    prompt: "生图提示词",
    reroll: "换一个人设",
    generate: "生成候选角色",
    candidateReady: "候选角色已生成 · 尚未加入当前项目",
    add: "加入角色工坊",
    adding: "正在加入...",
    added: "已加入角色工坊",
    download: "下载",
    downloading: "正在下载...",
    view: "查看角色资产",
    failed: "生成失败",
  }, [overseasProject]);

  const [gender, setGender] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [ethnicity, setEthnicity] = useState("");
  const [occupation, setOccupation] = useState("");
  const [direction, setDirection] = useState("");
  const [layoutMode, setLayoutMode] = useState(DEFAULT_LAYOUT_MODE);
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedCandidate, setGeneratedCandidate] = useState(null);
  const [addedCharacter, setAddedCharacter] = useState(null);
  const [adding, setAdding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const addingRef = useRef(false);

  const hasConstraints = Boolean(gender || ageRange || ethnicity.trim() || occupation.trim() || direction.trim());
  const busy = planning || generating || adding;

  const constraints = () => ({
    gender,
    ageRange,
    ethnicity: ethnicity.trim(),
    occupation: occupation.trim(),
    direction: direction.trim(),
    randomSeed: newSeed(),
  });

  const generateCandidate = async (nextPlan) => {
    if (!nextPlan?.name?.trim() || !nextPlan?.imagePrompt?.trim()) return;
    setGenerating(true);
    setError("");
    setGeneratedCandidate(null);
    setAddedCharacter(null);
    try {
      const candidate = await onGeneratePreview(nextPlan, {
        layoutMode,
        aspectRatio: RATIO_LAYOUTS.has(layoutMode) ? aspectRatio : undefined,
      });
      setGeneratedCandidate(candidate || null);
    } catch (err) {
      setError(err?.message || text.failed);
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const addCandidateToWorkshop = async () => {
    if (!generatedCandidate?.imageUrl || !plan || addedCharacter || addingRef.current) return;
    addingRef.current = true;
    setAdding(true);
    setError("");
    try {
      const saved = await onAddToWorkshop({
        ...generatedCandidate,
        ...copyPlan(plan),
        imageUrl: generatedCandidate.imageUrl,
      });
      setAddedCharacter(saved || null);
    } catch (err) {
      setError(err?.message || text.failed);
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };

  const downloadCandidate = async () => {
    const imageUrl = generatedCandidate?.imageUrl;
    if (!imageUrl) return;
    const safeName = String(plan?.name || "random-character").replace(/[\\/:*?"<>|]/g, "_");
    setDownloading(true);
    setError("");
    try {
      const response = await fetch(imageUrl, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const extension = blob.type.includes("jpeg") ? "jpg" : blob.type.includes("webp") ? "webp" : "png";
      downloadBlob(blob, `${safeName}.${extension}`);
    } catch (err) {
      try {
        downloadFile(imageUrl, `${safeName}.png`);
      } catch {
        setError((overseasProject ? "Download failed: " : "下载失败：") + (err?.message || "unknown error"));
      }
    } finally {
      setDownloading(false);
    }
  };

  const runCasting = async ({ autoGenerate = false } = {}) => {
    setPlanning(true);
    setError("");
    setGeneratedCandidate(null);
    setAddedCharacter(null);
    try {
      const nextPlan = copyPlan(await onCreatePlan(constraints()));
      if (!nextPlan.name.trim() || !nextPlan.imagePrompt.trim()) {
        throw new Error(overseasProject ? "The casting result is incomplete" : "选角结果缺少角色名称或生图提示词");
      }
      setPlan(nextPlan);
      if (autoGenerate) await generateCandidate(nextPlan);
    } catch (err) {
      setError(err?.message || text.failed);
    } finally {
      setPlanning(false);
    }
  };

  const updatePlan = (patch) => setPlan((current) => current ? { ...current, ...patch } : current);

  const genderOptions = [
    { value: "", label: text.random },
    { value: overseasProject ? "female" : "女性", label: text.female },
    { value: overseasProject ? "male" : "男性", label: text.male },
    { value: overseasProject ? "open" : "不限定", label: text.open },
  ];
  const ageOptions = [
    { value: "", label: text.random },
    { value: overseasProject ? "teen" : "少年", label: text.teen },
    { value: overseasProject ? "young adult" : "青年", label: text.young },
    { value: overseasProject ? "middle-aged" : "中年", label: text.middle },
    { value: overseasProject ? "senior" : "老年", label: text.senior },
  ];

  const segmented = (options, value, onChange) => (
    <div className="grid grid-cols-4 gap-1 rounded-lg p-1" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      {options.map((option) => {
        const active = value === option.value;
        return (
          <button
            key={`${option.value}-${option.label}`}
            type="button"
            disabled={busy}
            onClick={() => onChange(option.value)}
            className="min-w-0 rounded-md px-2 py-1.5 text-2xs font-bold transition-colors disabled:opacity-50"
            style={{
              color: active ? "white" : "var(--color-text-muted)",
              backgroundColor: active ? theme.colors.bluePrimary : "transparent",
            }}
            aria-pressed={active}
          >
            <span className="block truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4 lg:p-6" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      <div className="mx-auto grid min-h-full max-w-[1500px] gap-4 xl:grid-cols-[380px_minmax(0,1fr)]">
        <section className="flex flex-col rounded-xl border p-4" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <Sparkles size={16} />
              </span>
              <div>
                <h3 className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{text.title}</h3>
                <p className="text-2xs" style={{ color: hasConstraints ? theme.colors.bluePrimary : "var(--color-text-muted)" }}>
                  {hasConstraints ? text.constrained : text.blind}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.gender}</label>
              {segmented(genderOptions, gender, setGender)}
            </div>
            <div>
              <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.age}</label>
              {segmented(ageOptions, ageRange, setAgeRange)}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.region}</label>
                <input
                  value={ethnicity}
                  disabled={busy}
                  onChange={(event) => setEthnicity(event.target.value)}
                  placeholder={text.regionPlaceholder}
                  className="w-full rounded-lg px-3 py-2 text-xs outline-none disabled:opacity-50"
                  style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)" }}
                />
              </div>
              <div>
                <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.role}</label>
                <input
                  value={occupation}
                  disabled={busy}
                  onChange={(event) => setOccupation(event.target.value)}
                  placeholder={text.rolePlaceholder}
                  className="w-full rounded-lg px-3 py-2 text-xs outline-none disabled:opacity-50"
                  style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)" }}
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.direction}</label>
              <textarea
                rows={4}
                value={direction}
                disabled={busy}
                onChange={(event) => setDirection(event.target.value)}
                placeholder={text.directionPlaceholder}
                className="w-full resize-none rounded-lg px-3 py-2 text-xs leading-5 outline-none disabled:opacity-50"
                style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)" }}
              />
            </div>

            <div className="border-t pt-3" style={{ borderColor: "var(--color-border-primary)" }}>
              <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.layout}</label>
              <AppSelect
                value={layoutMode}
                disabled={busy}
                onChange={(event) => setLayoutMode(event.target.value)}
                className="w-full rounded-lg px-3 py-2 text-xs font-bold outline-none disabled:opacity-50"
                style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)" }}
              >
                {LAYOUT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{overseasProject ? option.en : option.zh}</option>)}
              </AppSelect>
              {RATIO_LAYOUTS.has(layoutMode) && (
                <div className="mt-2">
                  <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.ratio}</label>
                  <div className="flex flex-wrap gap-1">
                    {ASPECT_OPTIONS.map((ratio) => (
                      <button
                        key={ratio}
                        type="button"
                        disabled={busy}
                        onClick={() => setAspectRatio(ratio)}
                        className="rounded-full px-2.5 py-1 text-2xs font-bold disabled:opacity-50"
                        style={{
                          color: aspectRatio === ratio ? theme.colors.bluePrimary : "var(--color-text-muted)",
                          backgroundColor: aspectRatio === ratio ? `${theme.colors.bluePrimary}18` : "var(--color-bg-primary)",
                          border: `1px solid ${aspectRatio === ratio ? theme.colors.bluePrimary : "var(--color-border-primary)"}`,
                        }}
                      >
                        {ratio}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-2 flex items-center justify-between rounded-lg px-3 py-2" style={{ backgroundColor: "var(--color-bg-primary)" }}>
                <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>{text.quality}</span>
                <span className="text-xs font-bold" style={{ color: theme.colors.bluePrimary }}>{imageQualityTier}</span>
              </div>
            </div>
          </div>

          <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
            <button
              type="button"
              disabled={busy}
              onClick={() => runCasting({ autoGenerate: false })}
              className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-bold transition-all active:scale-[0.98] disabled:opacity-50"
              style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)" }}
            >
              {planning ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {planning ? text.planning : text.planOnly}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => runCasting({ autoGenerate: true })}
              className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-bold text-[var(--color-accent-contrast)] transition-all active:scale-[0.98] disabled:opacity-50"
              style={{ background: "var(--color-accent-primary)" }}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
              {planning ? text.planning : generating ? text.generating : text.planAndImage}
            </button>
          </div>
        </section>

        <section className="min-h-[560px] overflow-hidden rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          {!plan ? (
            <div className="flex h-full min-h-[560px] flex-col items-center justify-center px-6 text-center">
              <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full" style={{ color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <User size={25} />
              </span>
              <p className="text-sm font-bold" style={{ color: "var(--color-text-secondary)" }}>{planning ? text.planning : text.empty}</p>
              <p className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>{text.emptyHint}</p>
            </div>
          ) : (
            <div className="grid h-full min-h-0 lg:grid-cols-[minmax(0,1fr)_minmax(280px,38%)]">
              <div className="min-h-0 overflow-y-auto custom-scrollbar p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.name}</label>
                    <input
                      value={plan.name}
                      disabled={generating}
                      onChange={(event) => updatePlan({ name: event.target.value })}
                      className="w-full bg-transparent text-xl font-bold outline-none disabled:opacity-60"
                      style={{ color: "var(--color-text-primary)" }}
                    />
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => runCasting({ autoGenerate: false })}
                    className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold disabled:opacity-50"
                    style={{ color: theme.colors.bluePrimary, backgroundColor: `${theme.colors.bluePrimary}14`, border: `1px solid ${theme.colors.bluePrimary}55` }}
                  >
                    {planning ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    {text.reroll}
                  </button>
                </div>

                <div className="mt-3">
                  <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.aliases}</label>
                  <input
                    value={plan.aliases.join(overseasProject ? ", " : "、")}
                    disabled={generating}
                    onChange={(event) => updatePlan({ aliases: event.target.value.split(/[,，、]/).map((item) => item.trim()).filter(Boolean) })}
                    className="w-full rounded-lg px-3 py-2 text-xs outline-none disabled:opacity-60"
                    style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)" }}
                  />
                </div>

                {plan.concept && (
                  <div className="mt-4">
                    <div className="mb-1 text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.concept}</div>
                    <p className="text-xs leading-6" style={{ color: "var(--color-text-secondary)" }}>{plan.concept}</p>
                  </div>
                )}

                {Object.keys(plan.identity).length > 0 && (
                  <div className="mt-4">
                    <div className="mb-2 text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.identity}</div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {Object.entries(plan.identity).filter(([, value]) => value).map(([key, value]) => (
                        <div key={key} className="rounded-lg px-3 py-2" style={{ backgroundColor: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)" }}>
                          <div className="text-[10px] uppercase" style={{ color: "var(--color-text-muted)" }}>{key.replaceAll("_", " ")}</div>
                          <div className="mt-0.5 text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>{String(value)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {plan.visualTraits.length > 0 && (
                  <div className="mt-4">
                    <div className="mb-2 text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.traits}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {plan.visualTraits.map((trait, index) => (
                        <span key={`${trait}-${index}`} className="rounded-full px-2.5 py-1 text-2xs" style={{ color: theme.colors.bluePrimary, backgroundColor: `${theme.colors.bluePrimary}12`, border: `1px solid ${theme.colors.bluePrimary}33` }}>
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <label className="mb-1 block text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>{text.prompt}</label>
                  <textarea
                    rows={12}
                    value={plan.imagePrompt}
                    disabled={generating}
                    onChange={(event) => updatePlan({ imagePrompt: event.target.value })}
                    className="w-full resize-y rounded-lg px-3 py-2 text-xs leading-5 outline-none disabled:opacity-60"
                    style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)" }}
                  />
                </div>

                {error && (
                  <div className="mt-3 rounded-lg px-3 py-2 text-xs animate-in fade-in slide-in-from-top-1 duration-200" style={{ color: theme.colors.error, backgroundColor: `${theme.colors.error}12`, border: `1px solid ${theme.colors.error}55` }}>
                    {error}
                  </div>
                )}

                <button
                  type="button"
                  disabled={busy || !plan.name.trim() || !plan.imagePrompt.trim()}
                  onClick={() => generateCandidate(plan)}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-xs font-bold text-[var(--color-accent-contrast)] transition-all active:scale-[0.99] disabled:opacity-50"
                  style={{ background: "var(--color-accent-primary)" }}
                >
                  {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                  {generating ? text.generating : text.generate}
                </button>
              </div>

              <div className="relative flex min-h-[420px] items-center justify-center overflow-hidden border-l p-4" style={{ backgroundColor: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)" }}>
                {generatedCandidate?.imageUrl ? (
                  <div className="flex h-full w-full flex-col">
                    <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                      <img src={generatedCandidate.imageUrl} alt={generatedCandidate.name || plan.name} className="h-full w-full object-contain" />
                      <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full px-2 py-1 text-2xs font-bold" style={{ color: theme.colors.success, backgroundColor: "var(--overlay-heavy)" }}>
                        <Check size={11} /> {addedCharacter ? text.added : text.candidateReady}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={downloadCandidate}
                        disabled={downloading}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold disabled:opacity-50"
                        style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)" }}
                      >
                        {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                        {downloading ? text.downloading : text.download}
                      </button>
                      {addedCharacter ? (
                        <button
                          type="button"
                          onClick={() => onViewCharacter?.(addedCharacter)}
                          className="rounded-lg px-3 py-2 text-xs font-bold"
                          style={{ color: theme.colors.bluePrimary, backgroundColor: `${theme.colors.bluePrimary}14`, border: `1px solid ${theme.colors.bluePrimary}55` }}
                        >
                          {text.view}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={addCandidateToWorkshop}
                          disabled={adding}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold text-[var(--color-accent-contrast)] disabled:opacity-50"
                          style={{ background: "var(--color-accent-primary)" }}
                        >
                          {adding ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />}
                          {adding ? text.adding : text.add}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-center">
                    {generating ? <Loader2 size={28} className="animate-spin" style={{ color: theme.colors.bluePrimary }} /> : <Wand2 size={28} style={{ color: "var(--color-text-muted)" }} />}
                    <p className="mt-3 text-xs font-bold" style={{ color: "var(--color-text-muted)" }}>{generating ? text.generating : text.generate}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default RandomCastingWorkspace;
